import { intersection } from 'lodash';

import {
  AdminWorkspaceRoles,
  Roles,
  getRoleTranslationKey,
} from '~/data/user/role';
import { WorkspaceType } from '~/scenes/MyWorkspace/data';
import { AppModules, RoleGroups } from '~/services/Permissions';

export interface WidgetProps<Config extends {} = {}> {
  title?: string;
  config?: Config & {
    role?: AdminWorkspaceRoles;
    workspaceType?: WorkspaceType;
  };
  width: number;
}

export enum MyWorkspaceWidgetNames {
  AnnouncementsWidget = 'AnnouncementsWidget',
  MyTeamWidget = 'MyTeamWidget',
  ApprovalWidget = 'ApprovalWidget',
  TeamAbsencesTodayWidget = 'TeamAbsencesTodayWidget',
  TeamCompensationWidget = 'TeamCompensationWidget',
  TeamPerformanceWidget = 'TeamPerformanceWidget',
  MyTeamAbsencesWidget = 'MyTeamAbsencesWidget',
  TeamTimeManagementWidget = 'TeamTimeManagementWidget',
  TeamTrainingsWidget = 'TeamTrainingsWidget',
  TeamTasksWidget = 'TeamTasksWidget',
  TeamAnniversariesWidget = 'TeamAnniversariesWidget',
  MyDataWidget = 'MyDataWidget',
  MyAbsencesWidget = 'MyAbsencesWidget',
  MyCompensationWidget = 'MyCompensationWidget',
  MyPerformanceWidget = 'MyPerformanceWidget',
  MyNotificationsWidget = 'MyNotificationsWidget',
  MyTasksWidget = 'MyTasksWidget',
  QuickLinksWidget = 'QuickLinksWidget',
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // SolaIntelWidget = 'SolaIntelWidget',
}

export const configurableWidgets = [
  MyWorkspaceWidgetNames.MyTeamWidget,
  MyWorkspaceWidgetNames.TeamAbsencesTodayWidget,
  MyWorkspaceWidgetNames.TeamPerformanceWidget,
  MyWorkspaceWidgetNames.MyTeamAbsencesWidget,
  MyWorkspaceWidgetNames.TeamTimeManagementWidget,
  MyWorkspaceWidgetNames.TeamTrainingsWidget,
  MyWorkspaceWidgetNames.TeamTasksWidget,
  MyWorkspaceWidgetNames.TeamAnniversariesWidget,
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // MyWorkspaceWidgetNames.SolaIntelWidget,
];

/** Widget Type functions & data **/

export enum WidgetType {
  Company = 'Company',
  Team = 'Team',
  Personal = 'Personal',
}

type WidgetTypeTranslationType = {
  [key in WidgetType]: string;
};

export const widgetTypeTranslationKeys: WidgetTypeTranslationType = {
  [WidgetType.Company]: 'widgetType_Company',
  [WidgetType.Team]: 'widgetType_Team',
  [WidgetType.Personal]: 'widgetType_Personal',
};

export const getWidgetTypeTranslationKey = (widgetType: WidgetType): string => {
  return widgetTypeTranslationKeys[widgetType];
};

type WidgetTypeWidgets = {
  [key in WidgetType]: MyWorkspaceWidgetNames[];
};
export const widgetsByType: WidgetTypeWidgets = {
  [WidgetType.Company]: [MyWorkspaceWidgetNames.AnnouncementsWidget],
  [WidgetType.Personal]: [
    MyWorkspaceWidgetNames.MyDataWidget,
    MyWorkspaceWidgetNames.MyAbsencesWidget,
    MyWorkspaceWidgetNames.MyCompensationWidget,
    MyWorkspaceWidgetNames.MyPerformanceWidget,
    MyWorkspaceWidgetNames.MyNotificationsWidget,
    MyWorkspaceWidgetNames.MyTasksWidget,
    MyWorkspaceWidgetNames.QuickLinksWidget,
  ],
  [WidgetType.Team]: [
    MyWorkspaceWidgetNames.MyTeamWidget,
    MyWorkspaceWidgetNames.ApprovalWidget,
    MyWorkspaceWidgetNames.TeamAbsencesTodayWidget,
    MyWorkspaceWidgetNames.TeamCompensationWidget,
    MyWorkspaceWidgetNames.TeamPerformanceWidget,
    MyWorkspaceWidgetNames.MyTeamAbsencesWidget,
    MyWorkspaceWidgetNames.TeamTimeManagementWidget,
    MyWorkspaceWidgetNames.TeamTrainingsWidget,
    MyWorkspaceWidgetNames.TeamTasksWidget,
    MyWorkspaceWidgetNames.TeamAnniversariesWidget,
    /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
    // MyWorkspaceWidgetNames.SolaIntelWidget,
  ],
};

export const getWidgetsByType = (
  widgetType: WidgetType,
): MyWorkspaceWidgetNames[] => {
  return widgetsByType[widgetType];
};

export const getWidgetType = (widget: MyWorkspaceWidgetNames): WidgetType => {
  return Object.entries(widgetsByType)
    .filter(([_, widgets]) => widgets.includes(widget))
    .map(([type]) => type as WidgetType)[0];
};

/** Restricted Widgets functions & data **/

export const getRestrictedWidgetsRolesOptions = (
  subset?: 'all' | 'allFullHR' | 'allManagers',
) => {
  const allFullHRs: Roles[] = [
    Roles.HR_STAFF,
    Roles.HR_ADMIN,
    Roles.HR_ADMIN_LIMITED,
  ];
  const allManagers: Roles[] = [
    Roles.MANAGER,
    Roles.ALT_MANAGER,
    Roles.DEPUTY_MANAGER,
  ];

  switch (subset) {
    case 'allFullHR':
      return allFullHRs;
    case 'allManagers':
      return allManagers;
    default:
      return [...allFullHRs, ...allManagers];
  }
};

export const getRestrictedWidgetsWhiteListRoles = (): {
  [key in MyWorkspaceWidgetNames]?: Roles[];
} => {
  const restrictedWidgets = getWidgetsByType(WidgetType.Team);

  const allFullHRs: Roles[] = getRestrictedWidgetsRolesOptions('allFullHR');
  const allManagers: Roles[] = getRestrictedWidgetsRolesOptions('allManagers');

  const widgetManagerWhiteListing: {
    [key in MyWorkspaceWidgetNames]?: Roles[];
  } = {
    [MyWorkspaceWidgetNames.MyTeamWidget]: allManagers,
    [MyWorkspaceWidgetNames.ApprovalWidget]: allManagers,
    [MyWorkspaceWidgetNames.TeamAbsencesTodayWidget]: allManagers,
    [MyWorkspaceWidgetNames.TeamCompensationWidget]: [
      Roles.MANAGER,
      Roles.ALT_MANAGER,
    ],
    [MyWorkspaceWidgetNames.TeamPerformanceWidget]: allManagers,
    [MyWorkspaceWidgetNames.MyTeamAbsencesWidget]: allManagers,
    [MyWorkspaceWidgetNames.TeamTimeManagementWidget]: [
      Roles.MANAGER,
      Roles.ALT_MANAGER,
    ],
    [MyWorkspaceWidgetNames.TeamTrainingsWidget]: allManagers,
    [MyWorkspaceWidgetNames.TeamTasksWidget]: allManagers,
    [MyWorkspaceWidgetNames.TeamAnniversariesWidget]: allManagers,
    /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
    // [MyWorkspaceWidgetNames.SolaIntelWidget]: [Roles.MANAGER],
  };

  return Object.fromEntries(
    restrictedWidgets.map((widget) => {
      const managers = widgetManagerWhiteListing[widget] || [];
      return [widget, [...allFullHRs, ...managers]];
    }),
  );
};

export const getRestrictedWidgetsForRoles = (
  roles: Roles[],
): MyWorkspaceWidgetNames[] => {
  return Object.entries(getRestrictedWidgetsWhiteListRoles())
    .filter(
      ([_, whiteListRoles]) => intersection(roles, whiteListRoles).length > 0,
    )
    .map(([widget]) => widget as MyWorkspaceWidgetNames);
};

export const getUnrestrictedWidgets = (): MyWorkspaceWidgetNames[] => {
  // Company & Personal type widgets are always available for everyone
  return [
    ...getWidgetsByType(WidgetType.Company),
    ...getWidgetsByType(WidgetType.Personal),
  ];
};
export const getAllWidgetsForRoles = (
  roles: Roles[],
): MyWorkspaceWidgetNames[] => {
  return [...getUnrestrictedWidgets(), ...getRestrictedWidgetsForRoles(roles)];
};

type WidgetsAllowedRoles = {
  [key in MyWorkspaceWidgetNames]: RoleGroups[];
};

/** Role Group Widget functions & data **/

export const widgetsAllowedRoles: WidgetsAllowedRoles = {
  [MyWorkspaceWidgetNames.AnnouncementsWidget]: [
    RoleGroups.EMPLOYEE,
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.MyTeamWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.ApprovalWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamAbsencesTodayWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamCompensationWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamPerformanceWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.MyTeamAbsencesWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamTimeManagementWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamTrainingsWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamTasksWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.TeamAnniversariesWidget]: [
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  [MyWorkspaceWidgetNames.MyDataWidget]: [RoleGroups.EMPLOYEE, RoleGroups.HR],
  [MyWorkspaceWidgetNames.MyAbsencesWidget]: [
    RoleGroups.EMPLOYEE,
    RoleGroups.HR,
  ],
  [MyWorkspaceWidgetNames.MyCompensationWidget]: [
    RoleGroups.EMPLOYEE,
    RoleGroups.HR,
  ],
  [MyWorkspaceWidgetNames.MyPerformanceWidget]: [
    RoleGroups.EMPLOYEE,
    RoleGroups.HR,
  ],
  [MyWorkspaceWidgetNames.MyNotificationsWidget]: [
    RoleGroups.EMPLOYEE,
    RoleGroups.HR,
  ],
  [MyWorkspaceWidgetNames.MyTasksWidget]: [RoleGroups.EMPLOYEE, RoleGroups.HR],
  [MyWorkspaceWidgetNames.QuickLinksWidget]: [
    RoleGroups.EMPLOYEE,
    RoleGroups.HR,
    RoleGroups.STRAIGHT_MANAGER,
    RoleGroups.RESPONSIBLE_MANAGER,
  ],
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // [MyWorkspaceWidgetNames.SolaIntelWidget]: [
  //   RoleGroups.HR,
  //   RoleGroups.STRAIGHT_MANAGER,
  // ],
};

/** Module Dependent Widget functions & data **/

type ModuleDependentWidgets = {
  [key in AppModules]?: MyWorkspaceWidgetNames[];
};

export const moduleDependentWidgets: ModuleDependentWidgets = {
  [AppModules.COLLABORATION]: [
    MyWorkspaceWidgetNames.MyTasksWidget,
    MyWorkspaceWidgetNames.TeamTasksWidget,
  ],
  /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
  // [AppModules.SOLAINTEL]: [MyWorkspaceWidgetNames.SolaIntelWidget],
  [AppModules.WORK_TIME_REPORTING]: [
    MyWorkspaceWidgetNames.TeamTimeManagementWidget,
  ],
};

/**
 *  Filter array of Widgets according to their required Modules
 */
export const filterWidgetsByEnabledModules = (
  widgets: MyWorkspaceWidgetNames[],
  enabledModules: string[],
) => {
  const widgetsToFilter = Object.entries(moduleDependentWidgets)
    .filter(([moduleName]) => !enabledModules.includes(moduleName))
    .map(([_, moduleWidgets]) => moduleWidgets)
    .flat();

  return widgets.filter((w) => !widgetsToFilter.includes(w));
};

/**
 *  Get widgets intended for Role Group
 */
export const getWidgetsByRoleGroup = (
  roleGroup: RoleGroups,
): MyWorkspaceWidgetNames[] => {
  return Object.entries(widgetsAllowedRoles)
    .filter(([widget, widgetRoleGroups]) =>
      widgetRoleGroups.includes(roleGroup),
    )
    .map(([widget]) => widget as MyWorkspaceWidgetNames);
};

/**
 *  Get Widgets matching multiple Role Groups
 */
export const getWidgetsByRoleGroups = (
  roleGroups: RoleGroups[],
): MyWorkspaceWidgetNames[] => {
  return Object.entries(widgetsAllowedRoles)
    .filter(
      ([widget, widgetRoleGroups]) =>
        intersection(widgetRoleGroups, roleGroups).length > 0,
    )
    .map(([widget]) => widget as MyWorkspaceWidgetNames);
};

export const convertStringsToRoles = (strRoles: string[]): Roles[] => {
  return Object.values(Roles).filter((role) => strRoles.includes(role));
};

export const getAvailableWidgets = (
  roles: string[],
  enabledModules: string[],
): MyWorkspaceWidgetNames[] => {
  const rolesEnums: Roles[] = convertStringsToRoles(roles);

  // Get Widgets by Roles & Enabled modules
  return filterWidgetsByEnabledModules(
    getAllWidgetsForRoles(rolesEnums),
    enabledModules,
  );
};

export const getUserRoleByWorkspaceRole = (
  role: AdminWorkspaceRoles,
): Roles | undefined => {
  return Object.values(Roles).find((r) => r.toString() === role.toString());
};

export const getWorkspaceRoleTranslationKey = (
  role: AdminWorkspaceRoles,
): string => {
  const userRole = getUserRoleByWorkspaceRole(role);
  return !!userRole ? getRoleTranslationKey(userRole) : '';
};

export const getWorkspaceRolesbyUserRoles = (
  roles: Roles[] | string[],
): AdminWorkspaceRoles[] => {
  const useRoles = roles.map((r) => r.toString());
  const userWorkspaceRoles = Object.values(AdminWorkspaceRoles).filter((r) =>
    useRoles.includes(r.toString()),
  );

  return userWorkspaceRoles.includes(AdminWorkspaceRoles.EMPLOYEE)
    ? userWorkspaceRoles
    : [...userWorkspaceRoles, AdminWorkspaceRoles.EMPLOYEE];
};

export const getWorkspaceRoleByUserRole = (
  role: Roles,
): AdminWorkspaceRoles => {
  const wsRoles = getWorkspaceRolesbyUserRoles([role]);

  return wsRoles.length > 0 ? wsRoles[0] : AdminWorkspaceRoles.EMPLOYEE;
};

export const getWorkspaceRoleWidgets = (
  role: AdminWorkspaceRoles,
): MyWorkspaceWidgetNames[] => {
  const employeeRoleWidgets = [
    MyWorkspaceWidgetNames.AnnouncementsWidget,
    MyWorkspaceWidgetNames.QuickLinksWidget,
    MyWorkspaceWidgetNames.MyDataWidget,
    MyWorkspaceWidgetNames.MyAbsencesWidget,
    MyWorkspaceWidgetNames.MyCompensationWidget,
    MyWorkspaceWidgetNames.MyPerformanceWidget,
    MyWorkspaceWidgetNames.MyNotificationsWidget,
    MyWorkspaceWidgetNames.MyTasksWidget,
  ];

  const managerWidgets = [
    MyWorkspaceWidgetNames.AnnouncementsWidget,
    MyWorkspaceWidgetNames.ApprovalWidget,
    MyWorkspaceWidgetNames.QuickLinksWidget,
    MyWorkspaceWidgetNames.MyTeamWidget,
    MyWorkspaceWidgetNames.TeamAbsencesTodayWidget,
    MyWorkspaceWidgetNames.TeamCompensationWidget,
    MyWorkspaceWidgetNames.TeamPerformanceWidget,
    MyWorkspaceWidgetNames.MyTeamAbsencesWidget,
    MyWorkspaceWidgetNames.TeamTimeManagementWidget,
    MyWorkspaceWidgetNames.TeamTrainingsWidget,
    MyWorkspaceWidgetNames.TeamTasksWidget,
    MyWorkspaceWidgetNames.TeamAnniversariesWidget,
    /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
    // MyWorkspaceWidgetNames.SolaIntelWidget,
  ];

  const teamManagerWidgets = managerWidgets.filter(
    (w) =>
      ![
        MyWorkspaceWidgetNames.TeamCompensationWidget,
        MyWorkspaceWidgetNames.TeamTimeManagementWidget,
        /* Temporarily commented out: see ticket https://github.com/Solaforce/internal/issues/4212 */
        // MyWorkspaceWidgetNames.SolaIntelWidget,
      ].includes(w),
  );

  switch (role) {
    case AdminWorkspaceRoles.HR_ADMIN:
    case AdminWorkspaceRoles.HR_STAFF:
      return managerWidgets;
    case AdminWorkspaceRoles.MANAGER:
    case AdminWorkspaceRoles.ALT_MANAGER:
      return managerWidgets;
    case AdminWorkspaceRoles.DEPUTY_MANAGER:
      return teamManagerWidgets;
    case AdminWorkspaceRoles.EMPLOYEE:
    default:
      return employeeRoleWidgets;
  }
};

export const getRoleWidgets = (role: Roles): MyWorkspaceWidgetNames[] =>
  getWorkspaceRoleWidgets(getWorkspaceRoleByUserRole(role));

type WidgetsByWorkspaceRole = {
  [key in AdminWorkspaceRoles]?: MyWorkspaceWidgetNames[];
};

export const getWidgetsMappedByRoles = (): WidgetsByWorkspaceRole => {
  return Object.fromEntries(
    Object.values(AdminWorkspaceRoles).map((role) => {
      return [role, getWorkspaceRoleWidgets(role)];
    }),
  );
};

export const getWidgetsByRole = (
  widgetsByRoles: WidgetsByWorkspaceRole,
  role: AdminWorkspaceRoles,
): MyWorkspaceWidgetNames[] => {
  const widgets = widgetsByRoles[role];

  return !!widgets ? widgets : [];
};

export const getWidgetsByRoles = (
  widgetsByRoles: WidgetsByWorkspaceRole,
  roles: AdminWorkspaceRoles[],
): MyWorkspaceWidgetNames[] => {
  return roles.map((r) => getWidgetsByRole(widgetsByRoles, r)).flat();
};

export const getWidgetDataRequestRoles = (
  role?: AdminWorkspaceRoles,
): AdminWorkspaceRoles[] | undefined => {
  return !!role ? [role] : undefined;
};
